import React from "react";

// Category icons for the bidirectional connection redesign — one glyph per kind
// of configuration so a fully-expanded page can still be scanned by shape:
//   General (sliders) · Receives/data in (arrow into tray) · Sends/data out
//   (arrow out of tray) · Field mapping (crossing arrows).
// Stroke-based, currentColor, so they inherit the text color next to them.

type IconProps = { className?: string };
const base = "inline-block flex-none";

export const GeneralIcon = ({ className = "" }: IconProps) => (
    <svg className={`${base} ${className}`} width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
        <line x1="2" y1="4.5" x2="14" y2="4.5" /><circle cx="6" cy="4.5" r="1.8" fill="white" />
        <line x1="2" y1="11.5" x2="14" y2="11.5" /><circle cx="10.5" cy="11.5" r="1.8" fill="white" />
    </svg>
);

export const ReceivesIcon = ({ className = "" }: IconProps) => (
    <svg className={`${base} ${className}`} width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 2v7.5" /><path d="M5 7l3 3 3-3" />
        <path d="M2.5 11.5v2a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-2" />
    </svg>
);

export const SendsIcon = ({ className = "" }: IconProps) => (
    <svg className={`${base} ${className}`} width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 10V2.5" /><path d="M5 5.5l3-3 3 3" />
        <path d="M2.5 11.5v2a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-2" />
    </svg>
);

export const MappingIcon = ({ className = "" }: IconProps) => (
    <svg className={`${base} ${className}`} width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M2 5h7.5" /><path d="M7.5 2.5L10 5l-2.5 2.5" />
        <path d="M14 11H6.5" /><path d="M8.5 8.5L6 11l2.5 2.5" />
    </svg>
);

// Flow-map arrows carry no text label — the arrow's colour states the direction's
// status, and one of these two glyphs is the click target on the line: sliders
// where the direction can be configured, a cross where it can never be enabled.
export const ConfigureIcon = ({ className = "" }: IconProps) => (
    <svg className={`${base} ${className}`} width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
        <line x1="2.5" y1="5" x2="13.5" y2="5" /><circle cx="6" cy="5" r="1.7" fill="white" />
        <line x1="2.5" y1="11" x2="13.5" y2="11" /><circle cx="10" cy="11" r="1.7" fill="white" />
    </svg>
);

export const NotPossibleIcon = ({ className = "" }: IconProps) => (
    <svg className={`${base} ${className}`} width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
        <line x1="4.5" y1="4.5" x2="11.5" y2="11.5" /><line x1="11.5" y1="4.5" x2="4.5" y2="11.5" />
    </svg>
);

// Designer-style centered action pill (see "+ Add Rule" in the original mocks).
export const ActionPillButton = ({ label, onClick }: { label: string, onClick: () => void }) => (
    <div className="flex justify-center my-5">
        <button type="button" onClick={onClick}
            className="flex items-center gap-2 bg-dark-green text-white rounded-full px-5 py-2 text-sm font-semibold hover:bg-connection-green transition-colors">
            <span className="text-base leading-none">+</span> {label}
        </button>
    </div>
);
